// "Meta para aprovação" na home do DNA Connect: nota necessária por filho/disciplina (motor oficial).
import React, { useEffect, useState } from 'react';
import { ChevronDown, GraduationCap, Sparkles } from 'lucide-react';
import { C, StatusBadge } from '@/design-system/dna';

const API = process.env.REACT_APP_BACKEND_URL;
const COLOR = { verde: C.green, amarelo: C.amber, laranja: C.orange, vermelho: '#E2412B' };
const n = (v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
const short = (t) => (t || '').replace(' Trimestre', ' tri');

const need = (s) => {
  if (s.status === 'verde') return 'Média garantida';
  if (s.status === 'vermelho') return 'Acompanhar recuperação';
  return s.restantes.length > 1 ? `${n(s.necessaria)} em cada tri restante` : `${n(s.necessaria)} no ${short(s.restantes[0])}`;
};

function Child({ k }) {
  const [open, setOpen] = useState(false);
  const pending = k.subjects.filter((s) => s.status !== 'verde');
  const list = open ? k.subjects : pending.slice(0, 4);
  const first = k.student_name.split(' ')[0];
  return (
    <div className="rounded-2xl p-4" style={{ background: C.paper }} data-testid={`goal-child-${k.student_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[14px] font-bold" style={{ color: C.ink }}>{first} <span className="font-normal text-[12px]" style={{ color: C.muted }}>· {k.class_name}</span></p>
        <span className="text-[11.5px]" style={{ color: C.muted }}>{k.summary.verde} de {k.subjects.length} disciplinas com média garantida</span>
      </div>
      {!pending.length ? (
        <p className="mt-2 flex items-center gap-2 text-[13px] font-semibold" style={{ color: C.green }} data-testid={`goal-child-ok-${k.student_id}`}>
          <Sparkles className="h-4 w-4" /> {first} já atingiu a média em todas as disciplinas. Parabéns!
        </p>
      ) : (
        <p className="mt-1 text-[12.5px]" style={{ color: C.muted }}>
          {k.summary.vermelho ? `Em ${k.summary.vermelho} disciplina(s) a aprovação direta não é mais possível — a escola vai acompanhar a recuperação junto com vocês. ` : ''}
          Veja a nota que {first} precisa{k.next ? ` a partir do ${short(k.next)}` : ''} para fechar o ano com média 6,0:
        </p>
      )}
      {!!list.length && (
        <ul className="mt-2.5 grid gap-1.5 sm:grid-cols-2">
          {list.map((s) => (
            <li key={s.subject_name} className="flex items-center justify-between gap-2 rounded-xl bg-white px-3 py-2" data-testid={`goal-subject-${k.student_id}-${s.subject_name}`}>
              <span className="min-w-0 truncate text-[13px] font-semibold" style={{ color: C.ink }}>{s.subject_name}</span>
              <span className="shrink-0 text-[12.5px] font-extrabold tabular-nums" style={{ color: COLOR[s.status] }}>{need(s)}</span>
            </li>
          ))}
        </ul>
      )}
      {k.subjects.length > list.length || open ? (
        <button type="button" onClick={() => setOpen(!open)} className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: C.purple }} data-testid={`goal-toggle-${k.student_id}`}>
          {open ? 'Mostrar menos' : `Ver todas as ${k.subjects.length} disciplinas`} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      ) : null}
    </div>
  );
}

export function GoalNotice() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('familyToken');
    fetch(`${API}/api/dnaconnect/pedagogico/metas-aprovacao?token=${encodeURIComponent(token || '')}`)
      .then((r) => (r.ok ? r.json() : { children: [] })).then(setData).catch(() => setData({ children: [] }));
  }, []);
  if (!data?.children?.length) return null;
  const anyRisk = data.children.some((k) => k.summary.vermelho || k.summary.laranja);
  return (
    <section className="min-w-0 rounded-[24px] border bg-white p-5" style={{ borderColor: C.line }} data-testid="goal-notice">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="dna-kicker flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> Meta para aprovação · {data.year}</p>
          <h2 className="text-base md:text-lg font-extrabold" style={{ color: C.ink, fontFamily: 'Manrope, sans-serif' }}>O que falta para fechar o ano com média 6,0</h2>
        </div>
        <StatusBadge tone={anyRisk ? 'warning' : 'success'} testid="goal-notice-status">{anyRisk ? 'Vale atenção' : 'Tudo encaminhado'}</StatusBadge>
      </div>
      <div className="grid gap-3">{data.children.map((k) => <Child key={k.student_id} k={k} />)}</div>
      <p className="mt-3 text-[11px]" style={{ color: C.muted }}>Calculado com as notas oficiais lançadas pela escola (média dos 3 trimestres, arredondamento de 0,5). Atualiza a cada nova nota.</p>
    </section>
  );
}
